// Path: frontend/src/contexts/AuthContext.jsx

import React, { createContext, useState, useContext, useEffect } from "react";
import { authAPI, userAPI } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return false;
    }

    try {
      const response = await authAPI.getProfile();
      // console.log("Auth getProfile response:", response.data);
      if (response.data.user) {
        // Ensure we get the full user data including created_at
        setUser(response.data.user);

        // Load user settings after successful auth check
        try {
          const settingsResponse = await userAPI.getSettings();
          if (settingsResponse.data) {
            // Theme and language will be handled by their respective providers
            // since they watch for token changes
            setLoading(false);
            return true;
          }
        } catch (settingsError) {
          console.error(
            "Failed to load user settings during auth check:",
            settingsError
          );
        }
        setLoading(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Auth check failed:", error);
      // Only remove token if it's an authentication error (401)
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
      }
      // For other errors, keep the token and user state
      // This prevents logout on network errors or other temporary issues
      setLoading(false);
      return false;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      // console.log("Attempting login with:", credentials);

      const response = await authAPI.login(credentials);
      // console.log("Login response:", response.data);

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        setUser(response.data.user);

        // Load user settings after successful login
        try {
          const settingsResponse = await userAPI.getSettings();
          if (settingsResponse.data) {
            // Theme and language will be handled by their respective providers
            // since they watch for token changes
            return response.data.user;
          }
        } catch (settingsError) {
          console.error("Failed to load user settings:", settingsError);
        }
        return response.data.user;
      } else {
        throw new Error("No token received from server");
      }
    } catch (err) {
      console.error("Login error:", err);
      const message = err.response?.data?.message || "Login failed";
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      
      // Transform field names to match backend expectations
      const transformedData = {
        username: userData.email, // Use email as username if no username provided
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
        ...userData // Keep any other fields that might be passed
      };
      
      console.log('Transformed registration data:', {
        ...transformedData,
        password: '***' // Hide password in logs
      });
      
      const response = await authAPI.register(transformedData);

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        setUser(response.data.user);

        // Load user settings after successful registration
        try {
          const settingsResponse = await userAPI.getSettings();
          if (settingsResponse.data) {
            // Theme and language will be handled by their respective providers
            // since they watch for token changes
            return response.data.user;
          }
        } catch (settingsError) {
          console.error(
            "Failed to load user settings after registration:",
            settingsError
          );
        }
        return response.data.user;
      } else {
        throw new Error("No token received from server");
      }
    } catch (err) {
      console.error("Registration error:", err);
      const message = err.response?.data?.message || "Registration failed";
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("token");
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    loading,
    error,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

